import { queryOptions } from '@tanstack/react-query'
import { ApiError, apiExchange, apiGet } from '../../api/client'
import { saveBytes } from '../../api/inventoree'

/*
 * Furnizorii de suport notati dupa cazurile lor la furnizor.
 *
 *   GET /business-apps/vendors?days=&company=                  clasamentul
 *   GET /business-apps/vendors/{key}?days=&company=            un furnizor: aplicatii + cazuri
 *   GET /business-apps/vendors/export.csv?days=&company=&vendor=
 *   GET /business-apps/{id}/vendor-performance?days=           cardul de pe fisa aplicatiei
 *
 * Orele sunt ore de ceas, cu o zecimala; `null` = nu e din ce sa se socoteasca.
 */

export type Grade = 'good' | 'fair' | 'poor' | 'too_few'

export type VendorFigures = {
  cases: number
  open: number
  responded: number
  avgFirstResponseHours: number | null
  medianFirstResponseHours: number | null
  resolved: number
  avgResolutionHours: number | null
  withDeadline: number
  breached: number
  breachedPct: number | null
  escalatedL2: number
  escalatedL2Pct: number | null
  contractHours: number | null
  contractMin: number | null
  contractMax: number | null
  responseVsContract: number | null
  grade: Grade
}

export type VendorRow = VendorFigures & {
  key: string
  name: string
  names: string[]
  appCount: number
  companyCount: number
}

export type VendorApp = VendorFigures & {
  id: number
  name: string
  companyId: number
  companyName: string
  supportVendor: string
}

export type VendorCaseRow = {
  id: number
  ticketId: number
  appId: number
  appName: string
  companyName: string
  status: number
  statusLabel: string
  vendorRef: string
  level: number
  createdAt: string
  firstResponseHours: number | null
  resolutionHours: number | null
  contractHours: number | null
  breached: boolean
  open: boolean
}

type Choice = { value: string; label: string }

type Base = {
  ready: boolean
  message?: string
  days: number
  periods: number[]
  companyId: number
  companies: { id: number; name: string }[]
  grades: Choice[]
  minCases: number
}

export type VendorsPage = Base & { vendors: VendorRow[] }

export type VendorDetail = Base & {
  vendor?: VendorRow
  apps?: VendorApp[]
  cases?: VendorCaseRow[]
  casesShown?: number
}

export type AppVendorPerformance = {
  ready: boolean
  days: number
  periods?: number[]
  vendorKey?: string
  vendorName?: string
  grades?: Choice[]
  vendor?: VendorRow | null
  app?: VendorFigures
}

function params(days: number, company: number, vendor?: string): string {
  const q = new URLSearchParams()
  q.set('days', String(days))
  if (company > 0) q.set('company', String(company))
  if (vendor) q.set('vendor', vendor)
  return `?${q.toString()}`
}

export function vendorsQuery(days: number, company: number) {
  return queryOptions({
    queryKey: ['business-apps', 'vendors', days, company],
    queryFn: () => apiGet<VendorsPage>(`/business-apps/vendors${params(days, company)}`),
  })
}

export function vendorDetailQuery(key: string, days: number, company: number) {
  return queryOptions({
    queryKey: ['business-apps', 'vendors', 'detail', key, days, company],
    queryFn: () => apiGet<VendorDetail>(`/business-apps/vendors/${encodeURIComponent(key)}${params(days, company)}`),
  })
}

export function appVendorPerformanceQuery(appId: number, days: number) {
  return queryOptions({
    queryKey: ['business-apps', 'vendor-performance', appId, days],
    queryFn: () => apiGet<AppVendorPerformance>(`/business-apps/${appId}/vendor-performance?days=${days}`),
  })
}

/** "Export CSV": clasamentul, sau cu `vendor` cazurile acelui furnizor. */
export async function downloadVendorsCsv(days: number, company: number, vendor?: string): Promise<void> {
  const out = await apiExchange(`/business-apps/vendors/export.csv${params(days, company, vendor)}`, { method: 'GET' })
  if (!out.type.includes('csv')) {
    throw new ApiError(out.status || 500, 'The CSV could not be produced.')
  }
  saveBytes(out.bytes, out.type, out.filename, vendor ? `vendor_${vendor}.csv` : 'vendors.csv')
}

/** `vendors/?vendor=hotel-soft` - pagina furnizorilor, deschisa la unul. */
export const vendorsUrl = (key?: string, days?: number) => {
  const q = new URLSearchParams()
  if (key) q.set('vendor', key)
  if (days) q.set('days', String(days))
  const s = q.toString()
  return `/app/applications/vendors${s === '' ? '' : `?${s}`}`
}

export function hours(value: number | null): string {
  if (value === null) return '-'
  if (value >= 48) return `${(value / 24).toFixed(1)} d`
  return `${value.toFixed(1)} h`
}

export function pct(value: number | null): string {
  return value === null ? '-' : `${value}%`
}

export function contractText(v: Pick<VendorFigures, 'contractMin' | 'contractMax'>): string {
  if (v.contractMin === null || v.contractMax === null) return '-'
  return v.contractMin === v.contractMax ? `${v.contractMin} h` : `${v.contractMin}-${v.contractMax} h`
}

export const GRADE_LABEL: Record<Grade, string> = { good: 'Good', fair: 'Fair', poor: 'Poor', too_few: 'Too few cases' }

export const GRADE_PILL: Record<Grade, string> = { good: 'adm-pill-ok', fair: 'adm-pill-warn', poor: 'adm-pill-bad', too_few: '' }
